import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import Pagination from '../../components/common/Pagination';
import { BookOpen, Plus, Pencil, Trash2, Search, X, Save, Loader2, Download, Upload, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AbbreviationDictionaryPage = () => {
  const { toast } = useToast();
  const [abbreviations, setAbbreviations] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showAdd, setShowAdd] = useState(false);
  const [newAbbr, setNewAbbr] = useState('');
  const [newFull, setNewFull] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAbbr, setEditAbbr] = useState('');
  const [editFull, setEditFull] = useState('');
  const [saving, setSaving] = useState(false);

  // CSV import state
  const fileInputRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);  // each row: { row_num, abbreviation, full_form, status, message, conflict_id, existing_abbreviation, existing_full_form, action, included }
  const [previewSummary, setPreviewSummary] = useState({ ok: 0, duplicate_in_db: 0, duplicate_in_csv: 0, invalid: 0, total: 0 });
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPerPage] = useState(20);
  const [committing, setCommitting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAbbreviations = useCallback(async (searchTerm = '', page = 1, perPage = 20) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      params.set('page', page);
      params.set('per_page', perPage);
      const res = await axios.get(`${API}/api/admin-tools/abbreviations?${params}`, { headers });
      setAbbreviations(res.data.abbreviations || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch abbreviations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAbbreviations(search, currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      fetchAbbreviations(search, 1, itemsPerPage);
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const handleAdd = async () => {
    if (!newAbbr.trim() || !newFull.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/admin-tools/abbreviations`, {
        abbreviation: newAbbr.trim(),
        full_form: newFull.trim()
      }, { headers });
      toast({ title: "Added", description: `"${newAbbr.trim()}" added to dictionary` });
      setNewAbbr('');
      setNewFull('');
      setShowAdd(false);
      fetchAbbreviations(search, currentPage, itemsPerPage);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add';
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditAbbr(item.abbreviation);
    setEditFull(item.full_form);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAbbr('');
    setEditFull('');
  };

  const handleSaveEdit = async () => {
    if (!editAbbr.trim() || !editFull.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin-tools/abbreviations/${editingId}`, {
        abbreviation: editAbbr.trim(),
        full_form: editFull.trim()
      }, { headers });
      toast({ title: "Updated", description: "Abbreviation updated" });
      cancelEdit();
      fetchAbbreviations(search, currentPage, itemsPerPage);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update';
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.abbreviation}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin-tools/abbreviations/${item.id}`, { headers });
      toast({ title: "Deleted", description: `"${item.abbreviation}" removed` });
      fetchAbbreviations(search, currentPage, itemsPerPage);
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  // ---------- CSV IMPORT HANDLERS ----------
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/admin-tools/abbreviations/csv-template`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'abbreviation_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Error", description: "Failed to download template", variant: "destructive" });
    }
  };

  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({ title: "Invalid file", description: "Only .csv files are accepted", variant: "destructive" });
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/admin-tools/abbreviations/csv-parse`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      // Initialize action + included per row
      const rows = (res.data.preview || []).map((r) => ({
        ...r,
        included: r.status !== 'invalid' && r.status !== 'duplicate_in_csv',
        action: r.status === 'duplicate_in_db' ? 'skip' : (r.status === 'ok' ? 'insert' : 'skip'),
      }));
      setPreviewRows(rows);
      setPreviewSummary(res.data.summary || { ok: 0, duplicate_in_db: 0, duplicate_in_csv: 0, invalid: 0, total: 0 });
      setPreviewPage(1);
      setShowImportModal(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to parse CSV';
      toast({ title: "Parse failed", description: msg, variant: "destructive" });
    } finally {
      setParsing(false);
      // File is consumed in-memory by backend; clear local handle
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updatePreviewRow = (rowNum, patch) => {
    setPreviewRows((prev) => prev.map((r) => (r.row_num === rowNum ? { ...r, ...patch } : r)));
  };

  const setAllConflictsAction = (newAction) => {
    setPreviewRows((prev) => prev.map((r) => (r.status === 'duplicate_in_db' ? { ...r, action: newAction, included: newAction !== 'skip' } : r)));
  };

  const commitSummary = useMemo(() => {
    let toInsert = 0, toOverwrite = 0, toSkip = 0;
    for (const r of previewRows) {
      if (!r.included || r.action === 'skip') { toSkip++; continue; }
      if (r.action === 'insert') toInsert++;
      else if (r.action === 'overwrite') toOverwrite++;
    }
    return { toInsert, toOverwrite, toSkip };
  }, [previewRows]);

  const handleCommit = async () => {
    // Build payload
    const payload = previewRows
      .filter((r) => r.status !== 'invalid' && r.status !== 'duplicate_in_csv')
      .map((r) => {
        const action = !r.included ? 'skip' : r.action;
        return {
          abbreviation: r.abbreviation.trim(),
          full_form: r.full_form.trim(),
          action,
          conflict_id: r.conflict_id || null,
        };
      });
    if (payload.every((r) => r.action === 'skip')) {
      toast({ title: "Nothing to import", description: "All rows are set to skip.", variant: "destructive" });
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/api/admin-tools/abbreviations/csv-commit`, { rows: payload }, { headers });
      const { inserted = 0, overwritten = 0, skipped = 0, errors = [] } = res.data || {};
      toast({
        title: "Import complete",
        description: `${inserted} inserted, ${overwritten} overwritten, ${skipped} skipped${errors.length ? `, ${errors.length} errors` : ''}.`,
      });
      setShowImportModal(false);
      setPreviewRows([]);
      fetchAbbreviations(search, currentPage, itemsPerPage);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to commit import';
      toast({ title: "Commit failed", description: msg, variant: "destructive" });
    } finally {
      setCommitting(false);
    }
  };

  // Pagination slice for preview
  const previewTotal = previewRows.length;
  const previewSlice = previewRows.slice((previewPage - 1) * previewPerPage, previewPage * previewPerPage);

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto" data-testid="abbreviation-dictionary-page">
        <Card className="mb-6 border-l-4 border-l-indigo-600 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-indigo-600/5 to-violet-600/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-600 rounded-lg">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl sm:text-3xl text-indigo-700" data-testid="dictionary-title">
                  Abbreviation Dictionary
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  Manage maritime abbreviations used for semantic similarity matching
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Search + Add bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search abbreviations or full forms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          <Button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-abbreviation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            onClick={triggerFilePicker}
            disabled={parsing}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="import-csv-btn"
          >
            {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFilePick}
            className="hidden"
            data-testid="csv-file-input"
          />
        </div>

        {/* Add form */}
        {showAdd && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50/50">
            <CardContent className="pt-5 pb-4">
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Abbreviation</label>
                  <Input
                    placeholder="e.g. PSCO"
                    value={newAbbr}
                    onChange={(e) => setNewAbbr(e.target.value)}
                    data-testid="new-abbreviation-input"
                  />
                </div>
                <div className="flex-[2]">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Full Form</label>
                  <Input
                    placeholder="e.g. Port State Control Officer"
                    value={newFull}
                    onChange={(e) => setNewFull(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    data-testid="new-fullform-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAdd}
                    disabled={adding || !newAbbr.trim() || !newFull.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-new-btn"
                  >
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowAdd(false); setNewAbbr(''); setNewFull(''); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline" className="text-gray-600" data-testid="total-count">
            {totalItems} abbreviation{totalItems !== 1 ? 's' : ''}
          </Badge>
          {search && (
            <span className="text-sm text-gray-500">
              matching &quot;{search}&quot;
            </span>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="abbreviations-table">
                <thead>
                  <tr className="border-b bg-gray-50/50">
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[180px]">
                      Abbreviation
                    </th>
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Full Form
                    </th>
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[80px]">
                      Type
                    </th>
                    <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[100px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {abbreviations.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-400 text-sm">
                        {search ? 'No abbreviations match your search' : 'No abbreviations found'}
                      </td>
                    </tr>
                  ) : abbreviations.map((item) => (
                    <tr key={item.id} className="border-b last:border-b-0 hover:bg-gray-50/50 transition-colors">
                      {editingId === item.id ? (
                        <>
                          <td className="p-2">
                            <Input
                              value={editAbbr}
                              onChange={(e) => setEditAbbr(e.target.value)}
                              className="h-8 text-sm"
                              data-testid={`edit-abbr-${item.id}`}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              value={editFull}
                              onChange={(e) => setEditFull(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                              className="h-8 text-sm"
                              data-testid={`edit-full-${item.id}`}
                            />
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {item.is_default ? 'Default' : 'Custom'}
                            </Badge>
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={handleSaveEdit} disabled={saving}
                                className="h-7 w-7 p-0 text-green-600 hover:text-green-700">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEdit}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600">
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3">
                            <span className="font-mono text-sm font-semibold text-indigo-700">
                              {item.abbreviation}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-700">
                            {item.full_form}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className={`text-xs ${item.is_default ? 'text-gray-500' : 'text-indigo-600 border-indigo-200'}`}>
                              {item.is_default ? 'Default' : 'Custom'}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => startEdit(item)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-indigo-600"
                                data-testid={`edit-btn-${item.id}`}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(item)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                                data-testid={`delete-btn-${item.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(perPage) => {
                  setItemsPerPage(perPage);
                  setCurrentPage(1);
                }}
              />
            )}
          </Card>
        )}

        {/* Info */}
        <Card className="mt-6 bg-gray-50 border-gray-200">
          <CardContent className="pt-5 pb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">About the Abbreviation Dictionary</h4>
            <ul className="text-xs text-gray-600 space-y-1.5">
              <li>Abbreviations are expanded before computing semantic embeddings for better similarity matching.</li>
              <li>Example: A question containing &quot;OWS&quot; will be expanded to include &quot;Oily Water Separator&quot; before comparison.</li>
              <li>Changes take effect immediately — the in-memory cache is refreshed after every add/edit/delete/import.</li>
              <li><strong>Case is preserved exactly as you type it</strong> (e.g., &quot;MARPOL&quot; stays uppercase, &quot;Cyl.&quot; keeps its dot).</li>
              <li>Bulk import: download the CSV template, fill it, then click Import CSV to review before committing.</li>
            </ul>
          </CardContent>
        </Card>

        {/* CSV Import Review Modal */}
        <Dialog open={showImportModal} onOpenChange={(open) => { if (!committing) setShowImportModal(open); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="import-review-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-600" />
                Review Abbreviation Import
              </DialogTitle>
              <DialogDescription>
                File was parsed in-memory and discarded immediately — nothing is stored on the server until you confirm.
                Review the rows below, edit values inline if needed, then click &quot;Confirm Import&quot;.
              </DialogDescription>
            </DialogHeader>

            {/* Summary chips */}
            <div className="flex flex-wrap items-center gap-2 px-1">
              <Badge variant="outline" className="text-gray-700">Total parsed: {previewSummary.total}</Badge>
              <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
                <CheckCircle2 className="w-3 h-3 mr-1" /> New: {previewSummary.ok}
              </Badge>
              <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">
                <AlertTriangle className="w-3 h-3 mr-1" /> DB conflict: {previewSummary.duplicate_in_db}
              </Badge>
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                Duplicate in CSV: {previewSummary.duplicate_in_csv}
              </Badge>
              <Badge variant="outline" className="text-gray-500 border-gray-200">
                Invalid: {previewSummary.invalid}
              </Badge>

              {previewSummary.duplicate_in_db > 0 && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-gray-500">All conflicts:</span>
                  <Button size="sm" variant="outline" onClick={() => setAllConflictsAction('overwrite')}
                    className="h-7 px-2 text-xs" data-testid="conflicts-overwrite-all-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Overwrite all
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAllConflictsAction('skip')}
                    className="h-7 px-2 text-xs" data-testid="conflicts-skip-all-btn">
                    Skip all
                  </Button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-lg mt-3">
              <table className="w-full text-sm" data-testid="import-preview-table">
                <thead className="sticky top-0 bg-gray-50 border-b z-10">
                  <tr>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-12">Use</th>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-16">Row</th>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[180px]">Abbreviation</th>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase">Full Form</th>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[180px]">Status</th>
                    <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase w-[140px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {previewSlice.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-sm">No rows parsed.</td></tr>
                  ) : previewSlice.map((r) => {
                    const isConflict = r.status === 'duplicate_in_db';
                    const isInvalid = r.status === 'invalid' || r.status === 'duplicate_in_csv';
                    return (
                      <tr key={r.row_num}
                        className={`border-b transition-colors ${isConflict ? 'bg-red-50/60' : isInvalid ? 'bg-gray-50' : 'hover:bg-gray-50/50'}`}
                        data-testid={`preview-row-${r.row_num}`}>
                        <td className="p-2 align-top">
                          <Checkbox
                            checked={!!r.included}
                            disabled={isInvalid}
                            onCheckedChange={(v) => updatePreviewRow(r.row_num, { included: !!v })}
                            data-testid={`preview-include-${r.row_num}`}
                          />
                        </td>
                        <td className="p-2 align-top text-xs text-gray-500 font-mono">{r.row_num}</td>
                        <td className="p-2 align-top">
                          <Input
                            value={r.abbreviation}
                            disabled={isInvalid && r.status === 'duplicate_in_csv'}
                            onChange={(e) => updatePreviewRow(r.row_num, { abbreviation: e.target.value })}
                            className="h-8 text-sm font-mono"
                            data-testid={`preview-abbr-${r.row_num}`}
                          />
                          {isConflict && r.existing_abbreviation && (
                            <div className="text-[10px] text-red-600 mt-1 font-mono">
                              existing: {r.existing_abbreviation}
                            </div>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          <Input
                            value={r.full_form}
                            disabled={isInvalid && r.status === 'duplicate_in_csv'}
                            onChange={(e) => updatePreviewRow(r.row_num, { full_form: e.target.value })}
                            className="h-8 text-sm"
                            data-testid={`preview-full-${r.row_num}`}
                          />
                          {isConflict && r.existing_full_form && (
                            <div className="text-[10px] text-red-600 mt-1">
                              existing: {r.existing_full_form}
                            </div>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          {r.status === 'ok' && (
                            <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 text-xs">New</Badge>
                          )}
                          {r.status === 'duplicate_in_db' && (
                            <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50 text-xs">DB conflict</Badge>
                          )}
                          {r.status === 'duplicate_in_csv' && (
                            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-xs">
                              Duplicate in CSV
                            </Badge>
                          )}
                          {r.status === 'invalid' && (
                            <Badge variant="outline" className="text-gray-600 border-gray-200 bg-gray-50 text-xs">Invalid</Badge>
                          )}
                          {r.message && (
                            <div className="text-[10px] text-gray-500 mt-1">{r.message}</div>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          {r.status === 'ok' && (
                            <span className="text-xs text-green-700">Will insert</span>
                          )}
                          {r.status === 'duplicate_in_db' && (
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant={r.action === 'overwrite' ? 'default' : 'outline'}
                                onClick={() => updatePreviewRow(r.row_num, { action: 'overwrite', included: true })}
                                className={`h-7 px-2 text-xs ${r.action === 'overwrite' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                                data-testid={`row-overwrite-${r.row_num}`}>
                                Overwrite
                              </Button>
                              <Button size="sm" variant={r.action === 'skip' ? 'default' : 'outline'}
                                onClick={() => updatePreviewRow(r.row_num, { action: 'skip', included: false })}
                                className={`h-7 px-2 text-xs ${r.action === 'skip' ? 'bg-gray-500 hover:bg-gray-600' : ''}`}
                                data-testid={`row-skip-${r.row_num}`}>
                                Skip
                              </Button>
                            </div>
                          )}
                          {isInvalid && <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination + commit summary */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-3 px-1">
              <div className="text-xs text-gray-600">
                <span className="text-green-700 font-medium">{commitSummary.toInsert}</span> to insert, {' '}
                <span className="text-red-700 font-medium">{commitSummary.toOverwrite}</span> to overwrite, {' '}
                <span className="text-gray-500 font-medium">{commitSummary.toSkip}</span> to skip
              </div>
              {previewTotal > previewPerPage && (
                <Pagination
                  currentPage={previewPage}
                  totalItems={previewTotal}
                  itemsPerPage={previewPerPage}
                  onPageChange={(page) => setPreviewPage(page)}
                  onItemsPerPageChange={() => {}}
                />
              )}
            </div>

            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setShowImportModal(false)} disabled={committing}
                data-testid="cancel-import-btn">
                Cancel
              </Button>
              <Button onClick={handleCommit} disabled={committing || (commitSummary.toInsert + commitSummary.toOverwrite === 0)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-import-btn">
                {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirm Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AbbreviationDictionaryPage;
